import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home, Users, Leaf, Palette, CheckCircle, Hammer, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedSection from '../components/AnimatedSection';
import { constructionProjects } from '../mockData';

const HomePage = () => {
  const houseModels = [
    {
      id: 'mc72',
      name: 'Modèle MC72',
      title: 'Maison plain-pied MC72',
      type: 'Plain-pied • Traditionnelle',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800'
    },
    {
      id: 'mc88',
      name: 'Modèle MC88',
      title: 'Maison Etage MC88',
      type: 'Étage • Moderne',
      image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800'
    },
    {
      id: 'mce1',
      name: 'Modèle MCE 1',
      title: 'Maison individuelle plain-pied MCE1',
      type: 'Plain-pied • Moderne',
      image: 'https://images.unsplash.com/photo-1531971589569-0d9370cbe1e5?w=800'
    },
    {
      id: 'mce2',
      name: 'Modèle MCE 2',
      title: 'Maison Etage modèle MCE 2',
      type: 'Étage • Provençale • Moderne',
      image: 'https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?w=800'
    },
    {
      id: 'mc1',
      name: 'Modèle MC1',
      title: 'Maison plain-pied MC1',
      type: 'Plain-pied • Moderne • Contemporaine',
      image: 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?w=800'
    },
    {
      id: 'mc2',
      name: 'Modèle MC2',
      title: 'Maison individuelle plain-pied MC2',
      type: 'Plain-pied • Moderne',
      image: 'https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?w=800'
    }
  ];

  const services = [
    {
      icon: Home,
      title: 'Qualité',
      description: 'Vous recherchez un partenaire expert pour bâtir votre chalet ou votre résidence alpine ? Nous mettons notre expertise au service de votre sérénité depuis 1985.'
    },
    {
      icon: Users,
      title: 'Proximité',
      description: 'Vous avez besoin d\'être accompagné, informé, écouté car la construction de votre maison est votre projet de vie ? Nous le savons et nous serons à vos côtés !'
    },
    {
      icon: Leaf,
      title: 'Éco-responsabilité',
      description: 'Vous croyez à l\'importance de maîtriser son impact environnemental pour la protection de notre planète? Nous aussi !'
    },
    {
      icon: Palette,
      title: 'Personnalisation',
      description: 'Vous rêvez d\'une maison qui vous corresponde ? Nous réalisons votre maison, unique, personnalisée et adaptée à vos besoins, vos envies et vos modes de vie.'
    }
  ];

  const steps = [
    {
      title: 'Terrain',
      description: 'Nous sommes à vos côtés dans cette étape pour vous accompagner dans votre choix grâce à l\'étude et l\'analyse du terrain que vous aurez sélectionné.'
    },
    {
      title: 'Financement',
      description: 'MRTP SARL, votre constructeur alpin basé à Villars-Colmars, estime avec vous l\'ensemble des frais que vous devez prévoir pour la construction de votre maison.'
    },
    {
      title: 'Projet sur-mesure',
      description: 'Être libre de choisir avec la sécurité d\'être guidé et accompagné : nous sommes à vos côtés pour écouter vos souhaits et les intégrer dans un projet personnalisé ou entièrement sur-mesure à votre image.'
    },
    {
      title: 'Suivi & déroulement',
      description: 'Nous vous accompagnons à chaque étape de votre projet, nous mettons notre expertise au service de votre tranquillité et de votre sérénité.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Carousel with Slides */}
      <div className="relative h-[600px] overflow-hidden">
        {/* Slide 1 - 4 cadeaux */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div className="max-w-2xl">
              <div className="bg-[#c4d600] text-gray-800 inline-block px-6 py-2 rounded mb-6 font-bold text-lg">
                POUR NOS 40 ANS
              </div>
              <h2 className="text-white text-5xl md:text-6xl font-bold mb-6">
                4 cadeaux pour vous en 2025 !
              </h2>
              <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold text-lg px-8 py-6">
                <Link to="/maison-biosourcee">Je clique pour en profiter</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Title Section */}
      <AnimatedSection animation="fade-up">
        <div className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Constructeur de maisons individuelles sur-mesure dans les Alpes du Sud
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              MRTP SARL vous accompagne dans la réalisation de votre projet de maison personnalisé, adapté à votre terrain, selon vos goûts, besoins et impératifs financiers.
            </p>
            <Button asChild variant="outline" className="border-[#c4d600] text-[#c4d600] hover:bg-[#c4d600] hover:text-gray-800 font-semibold transition-all duration-300 hover:scale-105">
              <Link to="/projet-construction">Notre approche</Link>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* Video Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Construire votre projet de vie, étape par étape</h2>
          <div className="flex justify-center mt-8">
            <div className="w-full max-w-4xl aspect-video bg-gray-300 rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/NEAlHxWnp30"
                title="MRTP SARL"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* House Models Section */}
      <AnimatedSection animation="fade-up">
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#c4d600] font-semibold mb-2">NOTRE CATALOGUE D'INSPIRATION</p>
              <h2 className="text-4xl font-bold text-gray-800">Nos modèles de maisons</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {houseModels.map((model, index) => (
                <AnimatedSection 
                  key={model.id} 
                  animation="fade-up" 
                  delay={index * 100}
                  className="h-full"
                >
                  <Link
                    to={`/modele/${model.id}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 block h-full"
                  >
                    <div className="relative h-64 overflow-hidden bg-gray-200">
                      <img
                        src={model.image}
                        alt={model.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                          e.target.onerror = null;
                        }}
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-sm text-[#c4d600] font-semibold mb-1">{model.name}</p>
                      <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">{model.title}</h3>
                      <p className="text-sm text-gray-600">{model.type}</p>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>

          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              className="border-[#c4d600] text-[#c4d600] hover:bg-[#c4d600] hover:text-gray-800 font-semibold"
            >
              <Link to="/modeles">Découvrez notre catalogue</Link>
            </Button>
          </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Construction Projects Section */}
      <AnimatedSection animation="fade-up">
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#c4d600] font-semibold mb-2">NOS PROJETS EN COURS</p>
              <h2 className="text-4xl font-bold text-gray-800">Projets de construction</h2>
              <p className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">
                Découvrez nos projets de construction en cours dans les Alpes-de-Haute-Provence
              </p>
            </div>

            {constructionProjects && Array.isArray(constructionProjects) && constructionProjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {constructionProjects.map((project, index) => (
                  project && (
                  <AnimatedSection 
                    key={project.id} 
                    animation="fade-up" 
                    delay={index * 100}
                    className="h-full"
                  >
                    <Link
                      to={`/projet/${project.id}`}
                      className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 block h-full"
                    >
                      <div className="relative h-64 overflow-hidden bg-gray-200">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                            e.target.onerror = null;
                          }}
                        />
                        <div className="absolute top-4 right-4 bg-[#c4d600] text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                          {project.status}
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">
                          {project.title}
                        </h3>
                        {project.subtitle && (
                          <p className="text-sm text-gray-600 mb-3">{project.subtitle}</p>
                        )}
                        <div className="flex items-center text-sm text-gray-600 mb-3">
                          <MapPin className="w-4 h-4 mr-2 text-[#c4d600]" />
                          <span>{project.commune || project.location}</span>
                        </div>
                        <div className="flex items-center text-sm text-[#c4d600] font-semibold">
                          <Hammer className="w-4 h-4 mr-2" />
                          <span>{project.projectType}</span>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                  )
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">Aucun projet en cours pour le moment.</p>
              </div>
            )}
          </div>
        </div>
      </AnimatedSection>

      {/* Terrain Announcement */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-8 shadow-md">
            <p className="text-[#c4d600] font-semibold mb-2">VOTRE TERRAIN, VOTRE PROJET</p>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">NOS ANNONCES TERRAINS</h2>
            <h3 className="text-xl font-semibold mb-4">Vous cherchez un terrain pour construire votre maison idéale ?</h3>
            <p className="text-gray-600 mb-6">
              Découvrez dès maintenant notre rubrique dédiée aux annonces terrains. Accédez à une sélection de terrains soigneusement choisis : localités idéales, surfaces variées et opportunités exclusives. Cliquez pour explorer toutes nos annonces et trouver l'emplacement parfait pour votre projet avec MRTP SARL, votre constructeur de maisons individuelles dans les Alpes-de-Haute-Provence.
            </p>
            <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold">
              <Link to="/nos-terrains">Voir les terrains disponibles</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Survey Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Et vous, elle ressemble à quoi
            </h2>
            <h3 className="text-3xl font-bold text-[#c4d600] mb-6">votre maison idéale ?</h3>
            <p className="text-lg text-gray-600 mb-4">
              On ne construit pas des maisons au hasard.
            </p>
            <p className="text-lg text-gray-600 mb-4">
              On les construit pour vous. Avec vous.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Et pour bien faire, on commence par vous écouter.
            </p>
            <p className="text-gray-700 mb-8">
              Prenez 3 minutes pour nous dire ce qui compte vraiment pour vous dans un projet de maison. Merci 🙏
            </p>
            <Button 
              asChild 
              className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold text-lg px-8 py-6"
            >
              <a href="https://forms.gle/BzpLzb96QJcjKjZm8" target="_blank" rel="noopener noreferrer">
                👉 Je donne mon avis
              </a>
            </Button>
          </div>
          
          {/* Embedded Form Preview */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Construire mieux commence par vous écouter
            </h3>
            <p className="text-gray-600 mb-6">
              <strong>Bonjour,</strong><br/><br/>
              Chez <strong>MRTP SARL</strong>, on ne construit pas seulement des maisons. On construit des projets de vie, et chaque voix compte.<br/><br/>
              Ce questionnaire est là pour <strong>mieux vous comprendre</strong>, affiner ce que nous proposons, et faire en sorte que chaque étape – de la première prise de contact jusqu'à la remise des clés – soit plus claire, plus simple, plus fluide.<br/><br/>
              Vos retours sont <strong>inestimables</strong>. Ils nous permettent de nous améliorer, mais surtout de vous proposer une expérience à la hauteur de vos attentes.<br/><br/>
              Cela vous prendra <strong>moins de 3 minutes</strong>, et c'est précieux pour nous.<br/><br/>
              Merci infiniment pour votre temps 🙏<br/><br/>
              L'équipe MRTP SARL.
            </p>
            <div className="text-center">
              <Button 
                asChild 
                className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
              >
                <a href="https://forms.gle/BzpLzb96QJcjKjZm8" target="_blank" rel="noopener noreferrer">
                  Remplir le questionnaire
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Votre imagination Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-sm font-semibold text-gray-600 mb-2">Votre maison</h2>
              <h3 className="text-4xl font-bold text-gray-800 mb-6">Votre imagination</h3>
              <p className="text-lg text-gray-600 mb-6">
                Votre terrain est très étroit ou pentu ? Vous adorez les maisons uniques à l'architecture extra-contemporaine ? Vous avez besoin d'un espace dédié à votre activité professionnelle ? Nous sommes en mesure de faire évoluer tous nos modèles ou de concevoir votre maison 100% sur mesure.
              </p>
              <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold">
                <Link to="/maisons-sur-mesure">Découvrez nos réalisations sur mesure</Link>
              </Button>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl bg-gray-200">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800"
                alt="Votre imagination"
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                  e.target.onerror = null;
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">VOTRE CONSTRUCTEUR</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Une grande famille</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              MRTP SARL c'est d'abord une histoire de famille, puis une rencontre d'hommes et de femmes qui œuvrent avec passion, depuis 1985 pour bâtir la maison de vos rêves ! C'est aussi une aventure humaine où se mêlent professionnalisme, investissement, énergie, convivialité, bonne humeur, écoute, proximité, exigence et bienveillance.
            </p>
            <Button asChild variant="outline" className="mt-6 border-[#c4d600] text-[#c4d600] hover:bg-[#c4d600] hover:text-gray-800">
              <Link to="/a-propos">Qui sommes-nous ?</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <AnimatedSection animation="fade-up">
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#c4d600] font-semibold mb-2">VOS ATTENTES</p>
              <h2 className="text-4xl font-bold text-gray-800">Notre promesse</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <AnimatedSection 
                    key={index} 
                    animation="scale" 
                    delay={index * 100}
                    className="text-center"
                  >
                    <div className="group hover:transform hover:scale-105 transition-all duration-300">
                      <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg group-hover:rotate-6 transition-all duration-300">
                        <Icon className="w-8 h-8 text-gray-800" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#c4d600] transition-colors">{service.title}</h3>
                      <p className="text-gray-600 text-sm">{service.description}</p>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Service Proximity Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-gray-600 font-semibold mb-2">PLUS QU'UN SERVICE COMPLET</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Une prestation de proximité</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Depuis 1985, notre raison d'être est de vous permettre de construire votre maison l'esprit tranquille. Faites confiance à notre équipe de professionnels à votre écoute et à vos côtés dans chacune des étapes de votre projet !
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              'Gestion des démarches administratives',
              'Gestion de votre chantier',
              'Aide à la sélection de votre terrain',
              'Étude chiffrée de votre budget d\'investissement',
              'Élaboration de votre plan de maison'
            ].map((service, index) => (
              <Link
                key={index}
                to="/projet-construction"
                className="bg-gray-50 hover:bg-[#c4d600] p-6 rounded-lg text-center transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-800">
                  <CheckCircle className="w-8 h-8 text-[#c4d600] group-hover:text-white" />
                </div>
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-800">{service}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">votre projet, notre qualité</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Notre accompagnement dans votre rêve</h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-8">
              De la visite préliminaire de votre terrain jusqu'aux démarches administratives, vous construisez l'esprit tranquille grâce à l'écoute et aux compétences d'une équipe de professionnels dédiée à la construction de votre maison individuelle. Chaque projet peut être agrandi, réduit ou modifié selon vos goûts, besoins et impératifs financiers.
            </p>
            <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold">
              <Link to="/contact">Lancer mon projet</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {steps.map((step, index) => (
              <AnimatedSection 
                key={index} 
                animation="fade-up" 
                delay={index * 150}
                className="relative"
              >
                <div className="bg-gray-50 rounded-lg p-6 h-full hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 group">
                  <div className="w-12 h-12 bg-[#c4d600] rounded-full flex items-center justify-center mb-4 text-gray-800 font-bold text-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3 group-hover:text-[#c4d600] transition-colors">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-[#c4d600]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Vous souhaitez faire construire dans les Alpes du Sud ?
          </h2>
          <p className="text-gray-800 mb-8">
            Venez à la rencontre de notre équipe commerciale dans l'une de nos 4 agences situées près de chez vous.
          </p>
          <Button asChild variant="secondary" className="bg-white hover:bg-gray-100 text-gray-800 font-semibold">
            <Link to="/nos-agences">Découvrir nos agences</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;